import {
  READ_ALL_ALERTS,
  MARK_AS_READ_ALERTS,
  READING_ALERTS,
  SET_ALERTS_USER_ID,
  UPDATE_NEW_ALERTS,
  UPDATE_ALERT_COUNTS,
  type AlertCard,
  type AlertCounts,
  type AlertList,
} from "./alertsAction";

// MARK_AS_READ_ALERTS stamps a Date onto `seenAt`, where the query returns the
// field as a string. Nothing reads it — the server always sends null, and the
// GraphQL schema keeps the field only for shape parity with the old REST
// payload — so the stored card is the query's card with that one field widened.
// Recording the discrepancy beats changing behaviour inside a file rename.
export type StoredAlert = Omit<AlertCard, "seenAt"> & {
  seenAt: AlertCard["seenAt"] | Date;
};

export type AlertsState = {
  alerts: StoredAlert[];
  loading: boolean;
  userId: number | null;
  totalAlertsCount: number;
  totalUnreadAlertsCount: number;
};

// Only the actions this slice handles. Other slices' actions reach every
// reducer at runtime and fall through to `default`.
type AlertsAction =
  | { type: typeof READ_ALL_ALERTS; payload: AlertList }
  | { type: typeof MARK_AS_READ_ALERTS }
  | { type: typeof READING_ALERTS }
  | { type: typeof SET_ALERTS_USER_ID; payload: number }
  | { type: typeof UPDATE_NEW_ALERTS; payload: AlertList }
  | { type: typeof UPDATE_ALERT_COUNTS; payload: AlertCounts };

const INIT_STATE: AlertsState = {
  alerts: [],
  loading: false,
  userId: null,
  totalAlertsCount: 0,
  totalUnreadAlertsCount: 0,
};

const alertsReducer = (
  state: AlertsState = INIT_STATE,
  action: AlertsAction
): AlertsState => {
  switch (action.type) {
    case READ_ALL_ALERTS:
      return {
        ...state,
        alerts: [...state.alerts, ...action.payload.alerts],
        loading: false,
        totalAlertsCount: action.payload.totalAlertsCount,
        totalUnreadAlertsCount: action.payload.totalUnreadAlertsCount,
      };
    case MARK_AS_READ_ALERTS:
      return {
        ...state,
        alerts: state.alerts.map((alert) => ({
          ...alert,
          seenAt: new Date(),
          isSeen: true,
        })),
        totalUnreadAlertsCount: 0,
      };
    case READING_ALERTS:
      return {
        ...state,
        loading: true,
      };
    case SET_ALERTS_USER_ID:
      return {
        ...state,
        userId: action.payload,
      };
    case UPDATE_NEW_ALERTS:
      return {
        ...state,
        alerts: [...action.payload.alerts],
        loading: false,
        totalAlertsCount: action.payload.totalAlertsCount,
        totalUnreadAlertsCount: action.payload.totalUnreadAlertsCount,
      };
    case UPDATE_ALERT_COUNTS:
      // Counts only. `alerts` and `loading` are left exactly as they are so a
      // background refresh can neither truncate a scrolled reader nor flash a
      // loading spinner over their list.
      return {
        ...state,
        totalAlertsCount: action.payload.totalAlertsCount,
        totalUnreadAlertsCount: action.payload.totalUnreadAlertsCount,
      };
    default:
      return state;
  }
};

export default alertsReducer;
